/**
 * Per-element identity for the properties panel and the preview: the CSS selector
 * a user's custom styling targets, and the short name the panel's copy uses.
 *
 * Both tables are the prototype's `FIELD_META` (`app.jsx:1882–1898`) as data, which
 * is what spec R67 points at: the selector chip reads `<selector> { … }` and the
 * Label placeholder reads `e.g. <panel name>` (R43).
 */

import { FieldId } from '../types';

/**
 * The class the preview stamps on a field's block, and the selector the user's
 * custom CSS is scoped to (spec R67).
 *
 * Carried from the prototype verbatim, quirks included, because the chip is copy
 * the spec pins:
 * - `savingsAmt` and `savingsPct` share `.product-savings`, and `uom` and
 *   `currency` share `.product-currency` — as in the prototype, where both pairs
 *   render the same class. CSS typed for either half of a pair therefore reaches
 *   both; the composed stylesheet emits both rules in field order, so the result is
 *   deterministic rather than one of them silently winning.
 * - The one departure: the prototype's CTA selector is `.product-cta button`, which
 *   matches nothing here — the preview's CTA is a static facsimile, deliberately not
 *   a real `<button>` (see `FieldBlockContent`). It is `.product-cta` so R67's "apply
 *   the typed declarations to that element" actually holds.
 */
export const FIELD_SELECTORS: Record<FieldId, string> = {
  image: '.product-image',
  sku: '.product-sku',
  title: '.product-title',
  brand: '.product-brand',
  model: '.product-model',
  desc: '.product-description',
  listPrice: '.product-list-price',
  salePrice: '.product-sale-price',
  savingsAmt: '.product-savings',
  savingsPct: '.product-savings',
  pricePerUnit: '.product-price-per-unit',
  currency: '.product-currency',
  stock: '.product-stock',
  leadTime: '.product-lead-time',
  qtyAvail: '.product-qty-available',
  discontinued: '.product-discontinued',
  starRating: '.product-rating',
  reviewCount: '.product-review-count',
  moq: '.product-moq',
  qtyIncrement: '.product-qty-increment',
  preferred: '.product-preferred',
  badge: '.product-badge',
  promoMsg: '.product-promo',
  newArrival: '.product-new-arrival',
  bestSeller: '.product-best-seller',
  sponsored: '.product-sponsored',
  cta: '.product-cta',
  qtySelector: '.product-qty-selector',
};

/**
 * The element's short name, used where the panel's copy names the element inline —
 * the Label placeholder `e.g. List price` (spec R43) and the Custom styling
 * control's accessible name.
 *
 * Deliberately *not* the panel heading: that stays the catalogue's own field name
 * ("List Price", "SKU / Part Number"), which R63 has not amended.
 */
export const FIELD_PANEL_NAMES: Record<FieldId, string> = {
  image: 'Image',
  sku: 'SKU',
  title: 'Title',
  brand: 'Brand',
  model: 'Model number',
  desc: 'Description',
  listPrice: 'List price',
  salePrice: 'Sale price',
  savingsAmt: 'Savings amount',
  savingsPct: 'Savings %',
  pricePerUnit: 'Price per unit',
  currency: 'Currency',
  stock: 'Stock status',
  leadTime: 'Lead time',
  qtyAvail: 'Quantity in stock',
  discontinued: 'Discontinued',
  starRating: 'Star rating',
  reviewCount: 'Review count',
  moq: 'MOQ',
  qtyIncrement: 'Order qty increment',
  preferred: 'Preferred flag',
  badge: 'Badge',
  promoMsg: 'Promo message',
  newArrival: 'New arrival',
  bestSeller: 'Best seller rank',
  sponsored: 'Sponsored',
  cta: 'Add to Cart button',
  qtySelector: 'Quantity selector',
};

export const getFieldSelector = (fieldId: FieldId): string => FIELD_SELECTORS[fieldId];

/**
 * The same selector as a class name for the preview to stamp on the element.
 *
 * One derivation, in one place: the class the preview writes and the selector the
 * stylesheet generates then cannot drift apart, which is what makes the Custom
 * styling section's chip an honest promise (spec R67).
 */
export const getFieldClassName = (fieldId: FieldId): string =>
  FIELD_SELECTORS[fieldId].replace(/^\./, '');

export const getFieldPanelName = (fieldId: FieldId): string => FIELD_PANEL_NAMES[fieldId];

/**
 * The class wrapping the preview card, and the prefix every generated custom-CSS
 * selector carries (spec R68).
 *
 * One token, one place: `composeCardStylesheet` prefixes with it and `CardPreview`
 * stamps it, so the confinement guarantee cannot drift between the two.
 */
export const PREVIEW_SCOPE_CLASS = 'product-cards-preview-scope';

/**
 * Cap on one element's custom CSS text (threat model, abuse case 2).
 *
 * Also what keeps NFR A3's 100 ms keystroke budget reachable: every keystroke
 * re-parses and re-composes the card's whole stylesheet.
 */
export const MAX_CUSTOM_CSS_LENGTH = 2000;

/** Cap on how many declarations survive parsing for one element (same reason). */
export const MAX_CUSTOM_CSS_DECLARATIONS = 40;
